<template>
  <el-dialog
    :model-value="modelValue"
    title="选择商品卡片"
    width="700px"
    append-to-body
    destroy-on-close
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="notice-box mb-3">
      <div>1.商品卡片信息来自于抖音来客平台，下方可授权商品列表查询及商品卡片查询</div>
    </div>
    <div class="product-toolbar mb-3">
      <DyLifeBindButton :account-id="numAccountId" @bound="loadList" />
      <div class="product-search-group">
        <el-input
          v-model="productIdSearch"
          placeholder="输入商品ID精确搜索"
          clearable
          class="product-search"
          @keyup.enter="onSearch"
          @clear="onSearch"
        />
        <el-input
          v-model="titleSearch"
          placeholder="输入商品标题搜索"
          clearable
          class="product-search"
          @keyup.enter="onSearch"
          @clear="onSearch"
        />
        <el-button type="primary" @click="onSearch">搜索</el-button>
      </div>
    </div>

    <el-alert
      v-for="notice in searchNotices"
      :key="notice"
      :title="notice"
      type="warning"
      show-icon
      :closable="false"
      class="mb-3"
    />

    <DataTableWithPagination
      v-model:page="page"
      v-model:page-size="pageSize"
      :data="tableRows"
      row-key="product_id"
      :loading="loading"
      :total="total"
      :show-column-settings="false"
      :page-sizes="pageSizes"
      :show-total="false"
      :empty-text="emptyText"
      padding="0"
      @page-change="loadList"
      @size-change="loadList"
    >
      <el-table-column width="44">
        <template #default="{ row }">
          <el-radio
            :model-value="selectedProductId"
            :value="String(row.product_id)"
            class="product-radio"
            @change="selectedItem = row"
          />
        </template>
      </el-table-column>
      <el-table-column prop="product_id" label="商品ID" width="100" />
      <el-table-column prop="product_name" label="商品名称" />
      <el-table-column prop="price" label="商品价格" width="120" />
      <el-table-column label="图片" width="80">
        <template #default="{ row }">
          <el-avatar v-if="row.image" shape="square" :size="32" :src="row.image" />
          <el-avatar v-else shape="square" :size="32">
            <el-icon><Picture /></el-icon>
          </el-avatar>
        </template>
      </el-table-column>
    </DataTableWithPagination>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">取消</el-button>
      <el-button type="primary" :disabled="!selectedItem" @click="onConfirm">确认</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Picture } from '@element-plus/icons-vue';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import DyLifeBindButton from '@/components/DyLifeBindButton.vue';
import { apiGet } from '@/api';
import { PAGE_SIZE_OPTIONS } from '@/constants/api';
import { DY_PRODUCT_MAX_PAGE_SIZE, DY_PRODUCT_SCAN_LIMIT } from '@/constants/channelMaterials';
import type { DyProductListResponse } from '@/types/channelMaterials';

const props = defineProps<{
  modelValue: boolean;
  accountId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'confirm', item: Record<string, unknown>): void;
}>();

const numAccountId = computed(() => Number(props.accountId ?? 0));
const loading = ref(false);
const tableRows = ref<Record<string, unknown>[]>([]);
const page = ref(1);
const pageSize = ref(20);
const total = ref(0);
const selectedItem = ref<Record<string, unknown> | null>(null);
/**
 * 选中行的商品ID，供单选列回显。
 * 卡片一次只发一个商品，故用单选而非多选列；翻页后选中行不在当前页时自然不回显。
 */
const selectedProductId = computed(() =>
  selectedItem.value ? String(selectedItem.value.product_id) : '',
);

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      selectedItem.value = null;
      page.value = 1;
      loadList();
    }
  },
);

/** 每页条数选项：接口上限 50，超过会静默返回空列表 */
const pageSizes = computed(() =>
  PAGE_SIZE_OPTIONS.filter((size) => size <= DY_PRODUCT_MAX_PAGE_SIZE),
);

/** 商品ID 精确搜索：接口按 ID 在商品库里翻页查找 */
const productIdSearch = ref('');
/** 商品标题搜索：接口翻页扫描商品库做模糊匹配，命中结果仍按 page/page_size 分页返回 */
const titleSearch = ref('');

/**
 * 本次搜索的扫描状态，无搜索条件时为 null。
 * 后端搜索是翻页扫描商品库，扫描没走完时结果不可全信，需要如实告诉用户
 */
const scanState = ref<{ truncated: boolean; partial: boolean } | null>(null);

/** 扫描不完整时的提示，两种情况可能同时出现，故用数组逐条展示 */
const searchNotices = computed(() => {
  if (!scanState.value) return [];
  const notices: string[] = [];
  if (scanState.value.truncated) {
    notices.push(
      `商品数量较多，仅在前 ${DY_PRODUCT_SCAN_LIMIT} 个商品中搜索，未找到时可改用商品ID精确搜索`,
    );
  }
  if (scanState.value.partial) {
    notices.push('搜索过程中部分商品拉取失败，本次结果可能不完整，建议重新搜索');
  }
  return notices;
});

/** 扫描触顶时结果为空只说明「前 N 个里没有」，不能按「无此商品」展示 */
const emptyText = computed(() =>
  scanState.value?.truncated
    ? `仅在前 ${DY_PRODUCT_SCAN_LIMIT} 个商品中搜索，未找到匹配的商品`
    : '暂无数据',
);

/** 两个搜索条件都会让接口整表重查，需回到第一页 */
function onSearch() {
  page.value = 1;
  loadList();
}

async function loadList() {
  loading.value = true;
  try {
    const productId = productIdSearch.value.trim();
    const title = titleSearch.value.trim();
    const res = (await apiGet('/channel-materials/dy-product-list', {
      account_id: numAccountId.value,
      page: page.value,
      page_size: pageSize.value,
      ...(productId ? { product_id: productId } : {}),
      ...(title ? { title } : {}),
    })) as DyProductListResponse;
    const list = res.data ?? [];
    tableRows.value = list;
    scanState.value =
      productId || title ? { truncated: !!res.truncated, partial: !!res.partial } : null;
    /** page_size 校正只为「服务端收敛超限值」而设，故仅采纳仍属于可选项的回传值 */
    const returned = Number(res.page_size);
    const effectivePageSize = pageSizes.value.includes(returned) ? returned : pageSize.value;
    pageSize.value = effectivePageSize;
    /**
     * 搜索场景后端回传命中总数，直接采纳才能翻到第二页；
     * 其余场景接口不给 total，按 has_more 推算虚拟总数，让翻页按钮可用
     */
    if (typeof res.total === 'number') {
      total.value = res.total;
    } else {
      total.value = res.has_more
        ? page.value * effectivePageSize + 1
        : (page.value - 1) * effectivePageSize + list.length;
    }
  } finally {
    loading.value = false;
  }
}

function onConfirm() {
  if (!selectedItem.value) return;
  emit('confirm', selectedItem.value);
  emit('update:modelValue', false);
}
</script>

<style lang="scss" scoped>
/** 单选列只要圆点，去掉空 label 占位与右边距，圆点才能在窄列里居中 */
.product-radio {
  margin-right: 0;

  :deep(.el-radio__label) {
    display: none;
  }
}

/** 授权按钮靠左、精确搜索靠右，同排两端对齐 */
.product-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

/** margin-left:auto 把搜索整体推到行尾，不受左侧按钮数量影响 */
.product-search-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

/** 两个搜索框并排，弹窗宽 700px，各收窄以免把授权按钮挤出行外 */
.product-search {
  width: 180px;
}

.notice-box {
  padding: 8px 12px;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
  font-size: 12px;
  color: #409eff;
  line-height: 1.8;
}
</style>
